"use client";
import {
  AnimatePresence,
  motion,
  type Transition,
  type Variants,
} from "motion/react";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useId,
  ReactNode,
  useState,
  useCallback,
  RefObject,
} from "react";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { usePreventScroll } from "@/hooks/usePreventScroll";

const DialogContext = createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  dialogRef: RefObject<HTMLDialogElement | null>;
  variants: Variants;
  transition?: Transition;
  ids: {
    dialog: string;
    title: string;
    description: string;
  };
  onAnimationComplete: (definition: string) => void;
  handleTrigger: () => void;
} | null>(null);

const defaultVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.9,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0.9,
  },
};

const defaultTransition: Transition = {
  ease: "easeOut",
  duration: 0.2,
};

export type DialogProps = {
  children: ReactNode;
  variants?: Variants;
  transition?: Transition;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

function Dialog({
  children,
  variants = defaultVariants,
  transition = defaultTransition,
  defaultOpen,
  onOpenChange,
  open,
}: DialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen || false,
  );
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isOpen = open !== undefined ? open : uncontrolledOpen;

  // prevent scroll when dialog is open on iOS
  usePreventScroll({
    isDisabled: !isOpen,
  });

  const setIsOpen = useCallback(
    (value: boolean) => {
      setUncontrolledOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    const handleCancel = (e: Event) => {
      e.preventDefault();
      if (isOpen) {
        setIsOpen(false);
      }
    };

    dialog.addEventListener("cancel", handleCancel);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      document.body.classList.remove("overflow-hidden");
    };
  }, [dialogRef, isOpen, setIsOpen]);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, [isOpen]);

  const handleTrigger = () => {
    setIsOpen(true);
  };

  const onAnimationComplete = (definition: string) => {
    if (definition === "exit" && !isOpen) {
      dialogRef.current?.close();
    }
  };

  const baseId = useId();
  const ids = {
    dialog: `motion-ui-dialog-${baseId}`,
    title: `motion-ui-dialog-title-${baseId}`,
    description: `motion-ui-dialog-description-${baseId}`,
  };

  return (
    <DialogContext.Provider
      value={{
        isOpen,
        setIsOpen,
        dialogRef,
        variants,
        transition,
        ids,
        onAnimationComplete,
        handleTrigger,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
}

export type DialogTriggerProps = {
  children: ReactNode;
  className?: string;
};

function DialogTrigger({ children, className }: DialogTriggerProps) {
  const context = useContext(DialogContext);
  if (!context) throw new Error("DialogTrigger must be used within Dialog");

  return (
    <div
      onClick={context.handleTrigger}
      className={cn(
        "inline-flex items-center justify-center rounded-md text-sm font-medium",
        "transition-colors focus-visible:outline-hidden",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
    >
      {children}
    </div>
  );
}

export type DialogPortalProps = {
  children: ReactNode;
  container?: HTMLElement | null;
};

function DialogPortal({
  children,
  container = typeof window !== "undefined" ? document.body : null,
}: DialogPortalProps) {
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    setMounted(true);
    setPortalContainer(container || document.body);
    return () => setMounted(false);
  }, [container]);

  if (!mounted || !portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
}
export type DialogContentProps = {
  children: ReactNode;
  className?: string;
  container?: HTMLElement;
  withScrollbar?: boolean;
};

function DialogContent({
  children,
  className,
  innerClassName,
  backdropClassName,
  container,
  withScrollbar,
}: DialogContentProps & {
  innerClassName?: string;
  backdropClassName?: string;
}) {
  const context = useContext(DialogContext);
  if (!context) throw new Error("DialogContent must be used within Dialog");
  const {
    isOpen,
    setIsOpen,
    dialogRef,
    variants,
    transition,
    ids,
    onAnimationComplete,
  } = context;

  // Kliknięcie w ::backdrop natywnego <dialog> ma target = sam <dialog>, ale taki sam target
  // dostaje `click` po zaznaczaniu tekstu wewnątrz i puszczeniu przycisku poza okienkiem
  // (przeglądarka wysyła click na wspólnego przodka mousedown/mouseup). Dlatego zamykamy
  // dopiero wtedy, gdy i wciśnięcie, i puszczenie przycisku trafiło w backdrop.
  const pressedOnBackdrop = useRef(false);

  const content = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "linear" }}
            layout={false}
            className={cn(
              "fixed inset-0 z-1000000 bg-black/50 backdrop-blur-xs will-change-[opacity]",
              backdropClassName,
            )}
          />
          {/* 2. Your original dialog, with modified classNames */}
          <motion.dialog
            key={ids.dialog}
            ref={dialogRef as RefObject<HTMLDialogElement>}
            id={ids.dialog}
            aria-labelledby={ids.title}
            aria-describedby={ids.description}
            aria-modal="true"
            role="dialog"
            onPointerDown={(e) => {
              pressedOnBackdrop.current = e.target === dialogRef.current;
            }}
            onPointerUp={(e) => {
              const wasPressedOnBackdrop = pressedOnBackdrop.current;
              pressedOnBackdrop.current = false;

              if (wasPressedOnBackdrop && e.target === dialogRef.current) {
                setIsOpen(false);
              }
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={transition}
            layout={false}
            onAnimationComplete={onAnimationComplete}
            className={cn(
              // Add a z-index higher than the backdrop
              "fixed top-1/2 left-1/2 z-1000000 -translate-x-1/2 -translate-y-1/2 transform overflow-visible rounded-lg border-0 p-0 shadow-lg will-change-[opacity,transform]",
              // The backdrop:* classes are removed from here
              "open:flex open:flex-col",
              "font-inter",
              className,
            )}
          >
            <div
              className={cn(
                "h-auto min-h-full w-full overflow-hidden",
                innerClassName,
                withScrollbar &&
                  "default-scrollbar overflow-x-visible overflow-y-auto p-2",
              )}
            >
              {children}
            </div>
          </motion.dialog>
        </>
      )}
    </AnimatePresence>
  );

  return <DialogPortal container={container}>{content}</DialogPortal>;
}

export type DialogHeaderProps = {
  children: ReactNode;
  className?: string;
};

function DialogHeader({ children, className }: DialogHeaderProps) {
  return (
    <div className={cn("flex flex-col space-y-1.5", className)}>{children}</div>
  );
}

export type DialogTitleProps = {
  children: ReactNode;
  className?: string;
};

function DialogTitle({ children, className }: DialogTitleProps) {
  const context = useContext(DialogContext);
  if (!context) throw new Error("DialogTitle must be used within Dialog");

  return (
    <h2
      id={context.ids.title}
      className={cn("text-base font-medium", className)}
    >
      {children}
    </h2>
  );
}

export type DialogDescriptionProps = {
  children: ReactNode;
  className?: string;
};

function DialogDescription({ children, className }: DialogDescriptionProps) {
  const context = useContext(DialogContext);
  if (!context) throw new Error("DialogDescription must be used within Dialog");

  return (
    <p
      id={context.ids.description}
      className={cn("text-base text-zinc-500", className)}
    >
      {children}
    </p>
  );
}

export type DialogCloseProps = {
  className?: string;
  children?: ReactNode;
  disabled?: boolean;
};

function DialogClose({ className, children, disabled }: DialogCloseProps) {
  const context = useContext(DialogContext);
  if (!context) throw new Error("DialogClose must be used within Dialog");

  return (
    <button
      onClick={() => context.setIsOpen(false)}
      type="button"
      aria-label="Close dialog"
      className={cn(
        "absolute top-4 right-4 rounded-xs opacity-70 transition-opacity",
        "hover:opacity-100 focus:outline-hidden",
        "disabled:pointer-events-none",
        className,
      )}
      disabled={disabled}
    >
      {children || <X className="h-4 w-4 cursor-pointer" />}
      <span className="sr-only">Close</span>
    </button>
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
